import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { cn } from "../../utils/cn";
import { useTheme } from "../../context/ThemeContext";
import { useLocale } from "../../context/LocaleContext";
import { STRINGS } from "../../data/strings";
import { Tag } from "../atoms/Tag";
import { ProjectMeta } from "../molecules/ProjectMeta";
import { ProjectMedia } from "../molecules/ProjectMedia";
import { ProjectLinks } from "../molecules/ProjectLinks";

export function ProjectModal({ project, isOpen, onClose }) {
  const { theme } = useTheme();
  const { locale, t } = useLocale();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const strings = STRINGS[locale].sections.projects;
  const media = project?.media || [];
  const current = media[currentIndex];
  const titleText = project ? t(project.title) : "";

  useEffect(() => {
    setCurrentIndex(0);
    setIsFullscreen(false);
  }, [project?.id]);

  useEffect(() => {
    if (!isOpen) setIsFullscreen(false);
  }, [isOpen]);

  const next = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      if (media.length === 0) return;
      setCurrentIndex((prev) => (prev + 1) % media.length);
    },
    [media.length]
  );

  const prev = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      if (media.length === 0) return;
      setCurrentIndex((prev) => (prev - 1 + media.length) % media.length);
    },
    [media.length]
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (isFullscreen) setIsFullscreen(false);
        else onClose();
      }
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, isFullscreen, next, prev, onClose]);

  if (!project) return null;

  const captionText = current?.caption ? t(current.caption) : "";
  const isVideo = current?.type === "video";

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="relative w-full max-w-5xl rounded-2xl bg-zinc-950 border shadow-3xl flex flex-col md:flex-row max-h-[92vh] overflow-y-auto md:overflow-hidden"
            style={{
              borderColor: `${theme.accent}33`,
              boxShadow: `0 0 50px ${theme.accent}22`,
            }}
          >
            <button
              onClick={onClose}
              aria-label={strings.close}
              className="absolute top-4 right-4 z-[110] p-2 rounded-full bg-black/60 text-white/80 hover:text-white transition-all border hover:bg-black/85 backdrop-blur-md"
              style={{ borderColor: `${theme.accent}44` }}
            >
              <X size={20} />
            </button>

            <div
              className="relative w-full md:w-1/2 bg-black flex flex-col border-b md:border-b-0 md:border-r"
              style={{ borderColor: `${theme.accent}22` }}
            >
              <div className="relative w-full aspect-[4/3] md:aspect-auto md:flex-1 md:min-h-0 group overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0"
                  >
                    <ProjectMedia
                      item={current}
                      alt={`${titleText} - ${currentIndex + 1}`}
                      controls={isVideo}
                      autoPlay={false}
                      muted={isVideo}
                      loop={false}
                      onError={(e) => {
                        e.target.src =
                          "https://placehold.co/800x600/1a1a1a/ffffff?text=" +
                          encodeURIComponent(titleText);
                      }}
                    />
                  </motion.div>
                </AnimatePresence>

                {!isVideo && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsFullscreen(true);
                    }}
                    aria-label="Zoom"
                    className="absolute top-4 left-4 z-[5] p-2 rounded-full bg-black/60 text-white border opacity-80 hover:opacity-100 transition-opacity backdrop-blur-md"
                    style={{ borderColor: `${theme.accent}44` }}
                  >
                    <ZoomIn size={18} />
                  </button>
                )}

                {media.length > 1 && (
                  <>
                    <button
                      onClick={prev}
                      aria-label={strings.previousMedia}
                      className="absolute left-3 top-1/2 -translate-y-1/2 z-[5] p-2 rounded-full bg-black/60 text-white border md:opacity-0 md:group-hover:opacity-100 transition-opacity backdrop-blur-md"
                      style={{ borderColor: `${theme.accent}44` }}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={next}
                      aria-label={strings.nextMedia}
                      className="absolute right-3 top-1/2 -translate-y-1/2 z-[5] p-2 rounded-full bg-black/60 text-white border md:opacity-0 md:group-hover:opacity-100 transition-opacity backdrop-blur-md"
                      style={{ borderColor: `${theme.accent}44` }}
                    >
                      <ChevronRight size={20} />
                    </button>

                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-[5]">
                      {media.map((_, index) => (
                        <button
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentIndex(index);
                          }}
                          aria-label={`Media ${index + 1}`}
                          className={cn(
                            "h-1.5 rounded-full transition-all duration-300",
                            index === currentIndex ? "w-6" : "w-1.5 opacity-50"
                          )}
                          style={{
                            backgroundColor:
                              index === currentIndex ? theme.accent : "white",
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div
                className="px-5 py-4 md:px-6 md:py-5 border-t bg-black/40 backdrop-blur-md"
                style={{ borderColor: `${theme.accent}22` }}
              >
                <div className="flex items-center justify-between gap-3 mb-1.5">
                  <span
                    className="text-[10px] uppercase tracking-widest font-bold"
                    style={{ color: theme.accent }}
                  >
                    {isVideo ? "Video" : "Media"} {currentIndex + 1} / {media.length || 1}
                  </span>
                </div>
                <p className="text-zinc-300 text-sm md:text-[15px] leading-relaxed min-h-[2.5em]">
                  {captionText}
                </p>
              </div>
            </div>

            <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 md:overflow-y-auto bg-zinc-950 flex flex-col relative">
              <div className="flex flex-wrap gap-2 mb-5 relative z-10">
                {project.tags.map((tag) => (
                  <Tag key={tag} size="sm">
                    {tag}
                  </Tag>
                ))}
              </div>

              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-5 tracking-tight leading-tight relative z-10">
                {titleText}
              </h2>

              <p className="text-zinc-300 text-sm sm:text-base leading-relaxed mb-7 font-medium relative z-10">
                {t(project.longDescription || project.description)}
              </p>

              <ProjectMeta metadata={project.metadata} />

              {project.metadata?.links && (
                <div className="mt-7 relative z-10">
                  <ProjectLinks
                    links={project.metadata.links}
                    size="md"
                    variant="chip"
                    strings={strings}
                  />
                </div>
              )}

              <div className="mt-7 relative z-10">
                <button
                  onClick={onClose}
                  className="w-full py-3.5 rounded-xl text-black text-base font-bold transition-all active:scale-[0.98] hover:scale-[1.01]"
                  style={{
                    backgroundColor: theme.accent,
                    boxShadow: `0 0 20px ${theme.accent}44`,
                  }}
                >
                  {strings.close}
                </button>
              </div>
            </div>
          </motion.div>

          <AnimatePresence>
            {isFullscreen && current?.type === "image" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={() => setIsFullscreen(false)}
                className="fixed inset-0 z-[200] bg-black/95 flex flex-col items-center justify-center p-4 cursor-zoom-out"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFullscreen(false);
                  }}
                  aria-label={strings.close}
                  className="absolute top-4 right-4 z-[210] p-2 rounded-full bg-black/60 text-white border"
                  style={{ borderColor: `${theme.accent}66` }}
                >
                  <X size={22} />
                </button>

                <motion.img
                  key={`fs-${currentIndex}`}
                  src={current.src}
                  alt={`${titleText} - ${currentIndex + 1} fullscreen`}
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  onClick={(e) => e.stopPropagation()}
                  className="max-h-[85vh] max-w-full object-contain rounded"
                />

                {captionText && (
                  <p
                    onClick={(e) => e.stopPropagation()}
                    className="mt-4 max-w-3xl text-center text-zinc-200 text-sm md:text-base px-4 leading-relaxed"
                  >
                    {captionText}
                  </p>
                )}

                {media.length > 1 && (
                  <>
                    <button
                      onClick={prev}
                      aria-label={strings.previousMedia}
                      className="absolute left-4 top-1/2 -translate-y-1/2 z-[210] p-3 rounded-full bg-black/60 text-white border backdrop-blur-md"
                      style={{ borderColor: `${theme.accent}44` }}
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={next}
                      aria-label={strings.nextMedia}
                      className="absolute right-4 top-1/2 -translate-y-1/2 z-[210] p-3 rounded-full bg-black/60 text-white border backdrop-blur-md"
                      style={{ borderColor: `${theme.accent}44` }}
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </AnimatePresence>
  );
}
